import type { L10n } from '../types'
import { prototypeActorName, prototypeAdminRole } from './adminOperations'

/* ------------------------------------------------------------------ *
 * Navigation
 * ------------------------------------------------------------------ */

export interface AdminNavItem {
  id: string
  label: L10n
  to: string
  /** Sidebar grouping. */
  group: 'overview' | 'people' | 'content' | 'insight' | 'ops'
  badge?: number
}

export const adminNav: AdminNavItem[] = [
  { id: 'dashboard', label: { 'pt-BR': 'Dashboard', 'en-US': 'Dashboard' }, to: '/admin', group: 'overview' },
  { id: 'users', label: { 'pt-BR': 'Usuários', 'en-US': 'Users' }, to: '/admin/usuarios', group: 'people' },
  { id: 'team', label: { 'pt-BR': 'Equipe', 'en-US': 'Team' }, to: '/admin/equipe', group: 'people' },
  { id: 'roles', label: { 'pt-BR': 'Papéis e permissões', 'en-US': 'Roles and permissions' }, to: '/admin/papeis', group: 'people' },
  { id: 'content', label: { 'pt-BR': 'Conteúdo', 'en-US': 'Content' }, to: '/admin/conteudo', group: 'content' },
  { id: 'moderation', label: { 'pt-BR': 'Moderação', 'en-US': 'Moderation' }, to: '/admin/moderacao/posts', group: 'content', badge: 14 },
  { id: 'reports', label: { 'pt-BR': 'Denúncias', 'en-US': 'Reports' }, to: '/admin/denuncias', group: 'content', badge: 7 },
  { id: 'notifications', label: { 'pt-BR': 'Notificações', 'en-US': 'Notifications' }, to: '/admin/notificacoes', group: 'content' },
  { id: 'dailycheck', label: { 'pt-BR': 'Daily Check', 'en-US': 'Daily Check' }, to: '/admin/daily-check', group: 'insight' },
  { id: 'gamification', label: { 'pt-BR': 'Gamificação', 'en-US': 'Gamification' }, to: '/admin/gamificacao/xp', group: 'insight' },
  { id: 'performance', label: { 'pt-BR': 'Performance', 'en-US': 'Performance' }, to: '/admin/performance', group: 'insight' },
  { id: 'community', label: { 'pt-BR': 'Comunidade', 'en-US': 'Community' }, to: '/admin/comunidade', group: 'insight' },
  { id: 'exports', label: { 'pt-BR': 'Relatórios', 'en-US': 'Reports & export' }, to: '/admin/relatorios', group: 'ops' },
  { id: 'audit', label: { 'pt-BR': 'Auditoria', 'en-US': 'Audit log' }, to: '/admin/auditoria', group: 'ops' },
  { id: 'settings', label: { 'pt-BR': 'Configurações', 'en-US': 'Settings' }, to: '/admin/config', group: 'ops' },
]

export const adminNavGroups: { id: AdminNavItem['group']; label: L10n }[] = [
  { id: 'overview', label: { 'pt-BR': 'Visão geral', 'en-US': 'Overview' } },
  { id: 'people', label: { 'pt-BR': 'Pessoas', 'en-US': 'People' } },
  { id: 'content', label: { 'pt-BR': 'Conteúdo e moderação', 'en-US': 'Content and moderation' } },
  { id: 'insight', label: { 'pt-BR': 'Análise', 'en-US': 'Analytics' } },
  { id: 'ops', label: { 'pt-BR': 'Operação', 'en-US': 'Operations' } },
]

/* ------------------------------------------------------------------ *
 * Dashboard
 * ------------------------------------------------------------------ */

export interface AdminMetric {
  id: string
  label: L10n
  value: string
  delta?: number
  hint?: L10n
  accent?: boolean
}

export const dashboardMetrics: AdminMetric[] = [
  // Every KPI card uses a FIXED window stated in its label or hint; none follows the period selector.
  { id: 'dau', label: { 'pt-BR': 'DAU', 'en-US': 'DAU' }, value: '18.402', delta: 4.1, hint: { 'pt-BR': 'Usuários ativos hoje · vs. ontem', 'en-US': 'Active users today · vs. yesterday' }, accent: true },
  { id: 'checks', label: { 'pt-BR': 'Daily Checks hoje', 'en-US': 'Daily Checks today' }, value: '14.977', delta: 5.6, hint: { 'pt-BR': '81,4% do DAU · hoje', 'en-US': '81.4% of DAU · today' } },
  { id: 'completion', label: { 'pt-BR': 'Taxa de conclusão hoje', 'en-US': 'Completion rate today' }, value: '62,3%', delta: 1.8, hint: { 'pt-BR': 'Dias fechados em 100 Bee · hoje', 'en-US': 'Days closed at 100 Bee · today' } },
  { id: 'avgxp', label: { 'pt-BR': 'Bee médio hoje', 'en-US': 'Average Bee today' }, value: '74,2', delta: 0.9, hint: { 'pt-BR': 'Por usuário · base de 100 Bee', 'en-US': 'Per user · out of the 100 Bee base' } },
  { id: 'wau', label: { 'pt-BR': 'WAU', 'en-US': 'WAU' }, value: '52.118', delta: 2.4, hint: { 'pt-BR': 'Últimos 7 dias', 'en-US': 'Last 7 days' } },
  { id: 'mau', label: { 'pt-BR': 'MAU', 'en-US': 'MAU' }, value: '96.540', delta: 3.2, hint: { 'pt-BR': 'Últimos 30 dias', 'en-US': 'Last 30 days' } },
  { id: 'streaks', label: { 'pt-BR': 'Sequências ativas', 'en-US': 'Active streaks' }, value: '31.208', delta: 6.7, hint: { 'pt-BR': 'Agora', 'en-US': 'Right now' } },
  { id: 'new', label: { 'pt-BR': 'Novos usuários (7d)', 'en-US': 'New users (7d)' }, value: '3.914', delta: -2.1, hint: { 'pt-BR': 'Janela fixa de 7 dias', 'en-US': 'Fixed 7-day window' } },
]

export const dashboardOps: AdminMetric[] = [
  { id: 'queue', label: { 'pt-BR': 'Fila de moderação', 'en-US': 'Moderation queue' }, value: '14', hint: { 'pt-BR': 'Itens aguardando revisão · agora', 'en-US': 'Items awaiting review · now' }, accent: true },
  { id: 'pending', label: { 'pt-BR': 'Denúncias pendentes', 'en-US': 'Pending reports' }, value: '7', hint: { 'pt-BR': 'Mais antiga: 9h', 'en-US': 'Oldest: 9h' } },
  { id: 'posts', label: { 'pt-BR': 'Posts hoje', 'en-US': 'Posts today' }, value: '2.841', delta: 3.3, hint: { 'pt-BR': 'vs. ontem', 'en-US': 'vs. yesterday' } },
  { id: 'pulses', label: { 'pt-BR': 'Pulses hoje', 'en-US': 'Pulses today' }, value: '9.126', delta: 8.2, hint: { 'pt-BR': 'vs. ontem', 'en-US': 'vs. yesterday' } },
]

export const totalUsers = '128.430'

/** 30 days of platform series, oldest first. */
export const activitySeries = Array.from({ length: 30 }, (_, i) => {
  const wave = Math.sin(i / 3.4)
  return {
    day: `${((i + 24) % 30) + 1}`,
    dau: Math.round(16800 + wave * 900 + i * 55),
    checks: Math.round(13400 + wave * 800 + i * 48),
    posts: Math.round(2500 + wave * 240 + i * 11),
  }
})

/** 90 days of daily sample data (daily average per point), oldest first. */
export const activitySeries90 = Array.from({ length: 90 }, (_, i) => {
  const wave = Math.sin(i / 3.4)
  return {
    day: `D-${89 - i}`,
    dau: Math.round(14200 + wave * 900 + i * 48),
    checks: Math.round(11300 + wave * 800 + i * 42),
  }
})

/** 12 months of sample data (monthly daily averages), oldest first. */
export const activitySeries12m = [
  'out', 'nov', 'dez', 'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set',
].map((month, i) => ({
  day: month,
  dau: Math.round(9200 + i * 780 + Math.sin(i / 1.8) * 420),
  checks: Math.round(7100 + i * 650 + Math.sin(i / 1.8) * 360),
}))

/**
 * Dashboard period scope. Only metrics and charts marked `period` follow the
 * top period selector; `fixed` metrics always keep their own window.
 */
export type MetricScope = 'fixed' | 'period'

/* ------------------------------------------------------------------ *
 * Users
 * ------------------------------------------------------------------ */

export type UserStatus = 'active' | 'inactive' | 'suspended' | 'new'
export type UserRole = 'user' | 'admin'

export interface AdminUser {
  id: string
  name: string
  username: string
  email: string
  status: UserStatus
  role: UserRole
  registered: string
  lastActive: string
  streak: number
  reports: number
  moderationActions: number
}

const seedNames: [string, string][] = [
  ['Ana Ribeiro', 'ana.ribeiro'],
  ['Marcos Vinícius', 'marcos.v'],
  ['Helena Castro', 'helena.castro'],
  ['Rafael Nunes', 'rafa.nunes'],
  ['Juliana Prado', 'ju.prado'],
  ['Tiago Moreira', 'tiago.m'],
  ['Beatriz Lemos', 'bia.lemos'],
  ['Caio Ferraz', 'caio.ferraz'],
  ['Larissa Amaral', 'lari.amaral'],
  ['Diego Sampaio', 'diego.sampaio'],
  ['Renata Duarte', 'renata.d'],
  ['Felipe Andrade', 'felipe.andrade'],
  ['Camila Rocha', 'camila.rocha'],
  ['Otávio Bruno', 'otavio.bruno'],
  ['Sofia Meireles', 'sofia.m'],
  ['Gustavo Pinheiro', 'gu.pinheiro'],
  ['Paula Antunes', 'paula.antunes'],
  ['Henrique Sales', 'rique.sales'],
  ['Vivian Braga', 'vivian.braga'],
  ['Leonardo Diniz', 'leo.diniz'],
  ['Mariana Lopes', 'mari.lopes'],
  ['Eduardo Vaz', 'edu.vaz'],
  ['Clara Bonfim', 'clara.bonfim'],
  ['Rodrigo Tavares', 'rodrigo.t'],
]

const statuses: UserStatus[] = ['active', 'active', 'active', 'new', 'inactive', 'suspended']

export const adminUsers: AdminUser[] = seedNames.map(([name, username], i) => ({
  id: `u${i + 1}`,
  name,
  username: `@${username}`,
  email: `${username.replace('.', '')}@exemplo.com`,
  status: statuses[i % statuses.length],
  role: i === 0 || i === 7 ? 'admin' : 'user',
  registered: `${String((i % 28) + 1).padStart(2, '0')}/0${(i % 9) + 1}/2026`,
  lastActive: i % 6 === 4 ? 'há 22 dias' : i % 6 === 5 ? 'há 2 meses' : `há ${(i % 9) + 1}h`,
  streak: i % 6 === 4 || i % 6 === 5 ? 0 : (i * 7) % 63,
  reports: i % 5 === 0 ? (i % 3) + 1 : 0,
  moderationActions: i % 6 === 5 ? 3 : i % 4 === 0 ? 1 : 0,
}))

/* ------------------------------------------------------------------ *
 * Moderation and reports
 * ------------------------------------------------------------------ */

export type ContentKind = 'post' | 'pulse' | 'comment'
export type ModerationStatus = 'pending' | 'reviewing' | 'approved' | 'hidden' | 'removed' | 'escalated'

export interface ModerationItem {
  id: string
  kind: ContentKind
  authorId: string
  excerpt: string
  category: string
  published: string
  reports: number
  reason?: L10n
  status: ModerationStatus
}

const reasons: L10n[] = [
  { 'pt-BR': 'Conteúdo enganoso sobre saúde', 'en-US': 'Misleading health content' },
  { 'pt-BR': 'Spam ou promoção não autorizada', 'en-US': 'Spam or unauthorized promotion' },
  { 'pt-BR': 'Assédio nos comentários', 'en-US': 'Harassment in comments' },
  { 'pt-BR': 'Imagem imprópria', 'en-US': 'Inappropriate image' },
  { 'pt-BR': 'Promessa de resultado sem base', 'en-US': 'Unsupported results claim' },
]

const excerpts: Record<ContentKind, string[]> = {
  post: [
    'Fechei o 21k em 1h47 depois de oito semanas de base aeróbica.',
    'Protocolo de suplementação que triplicou meu rendimento em 5 dias.',
    'Sequência de 30 dias completa. O sono foi o hábito mais difícil.',
    'Meu primeiro treino de força depois da lesão. Cargas leves, foco na técnica.',
    'Desafio: quem faz jejum de 48h comigo nesta semana?',
    'Split negativo nos últimos 3 km. Pace médio 4:58.',
  ],
  pulse: [
    'Treino de 52 min · zona 2',
    'Refeição pós-treino',
    'Alongamento e respiração',
    'Pedal de 38 km',
    'Natação · 1.500 m',
    'Caminhada de recuperação',
  ],
  comment: [
    'Isso não funciona, você está enganando as pessoas.',
    'Parabéns pela consistência, inspirador demais.',
    'Compre pelo meu link e ganhe desconto 🔥',
    'Que evolução no pace, muito bom!',
    'Ninguém aguenta te ver postando isso todo dia.',
    'Vou tentar esse protocolo hoje mesmo.',
  ],
}

const modStatuses: ModerationStatus[] = ['pending', 'pending', 'reviewing', 'approved', 'hidden', 'escalated']

function buildModeration(kind: ContentKind): ModerationItem[] {
  return excerpts[kind].map((excerpt, i) => ({
    id: `${kind}-${i + 1}`,
    kind,
    authorId: adminUsers[(i * 3) % adminUsers.length].id,
    excerpt,
    category: ['Corrida', 'Nutrição', 'Mindfulness', 'Musculação', 'Lifestyle', 'Ciclismo'][i % 6],
    published: `${String((i % 20) + 1).padStart(2, '0')}/09/2026`,
    reports: i % 2 === 0 ? (i % 4) + 1 : 0,
    reason: i % 2 === 0 ? reasons[i % reasons.length] : undefined,
    status: modStatuses[i % modStatuses.length],
  }))
}

export const moderationQueue: Record<ContentKind, ModerationItem[]> = {
  post: buildModeration('post'),
  pulse: buildModeration('pulse'),
  comment: buildModeration('comment'),
}

export type ReportStatus = 'pending' | 'reviewing' | 'resolved' | 'rejected'
export type ReportTarget = 'post' | 'pulse' | 'comment' | 'user'

export interface AdminReport {
  id: string
  target: ReportTarget
  excerpt: string
  authorId: string
  reporterId: string
  reason: L10n
  date: string
  status: ReportStatus
  resolution?: L10n
}

export const adminReports: AdminReport[] = [
  { id: 'R-2841', target: 'post', excerpt: 'Protocolo de suplementação que triplicou meu rendimento em 5 dias.', authorId: 'u2', reporterId: 'u5', reason: reasons[0], date: '22/09/2026 08:14', status: 'pending' },
  { id: 'R-2840', target: 'comment', excerpt: 'Isso não funciona, você está enganando as pessoas.', authorId: 'u3', reporterId: 'u9', reason: reasons[2], date: '22/09/2026 07:02', status: 'reviewing' },
  { id: 'R-2839', target: 'user', excerpt: '@caio.ferraz', authorId: 'u8', reporterId: 'u4', reason: reasons[1], date: '21/09/2026 22:40', status: 'pending' },
  { id: 'R-2838', target: 'pulse', excerpt: 'Refeição pós-treino', authorId: 'u6', reporterId: 'u11', reason: reasons[3], date: '21/09/2026 19:11', status: 'resolved', resolution: { 'pt-BR': 'Conteúdo mantido. Denúncia sem fundamento.', 'en-US': 'Content kept. Report unfounded.' } },
  { id: 'R-2837', target: 'post', excerpt: 'Desafio: quem faz jejum de 48h comigo nesta semana?', authorId: 'u5', reporterId: 'u2', reason: reasons[4], date: '21/09/2026 16:28', status: 'resolved', resolution: { 'pt-BR': 'Post removido e autor advertido.', 'en-US': 'Post removed and author warned.' } },
  { id: 'R-2836', target: 'comment', excerpt: 'Compre pelo meu link e ganhe desconto 🔥', authorId: 'u7', reporterId: 'u13', reason: reasons[1], date: '20/09/2026 11:55', status: 'rejected', resolution: { 'pt-BR': 'Fora do escopo das diretrizes.', 'en-US': 'Outside the scope of the guidelines.' } },
  { id: 'R-2835', target: 'post', excerpt: 'Meu primeiro treino de força depois da lesão.', authorId: 'u4', reporterId: 'u15', reason: reasons[0], date: '20/09/2026 09:03', status: 'pending' },
]

/* ------------------------------------------------------------------ *
 * Daily Check analytics (aggregate only)
 * ------------------------------------------------------------------ */

export const habitCompletion = [
  { habit: 'Hidratação', xp: 15, completion: 88, dropoff: 12 },
  { habit: 'Movimento', xp: 25, completion: 71, dropoff: 29 },
  { habit: 'Mente', xp: 15, completion: 64, dropoff: 36 },
  { habit: 'Nutrição', xp: 10, completion: 79, dropoff: 21 },
  { habit: 'Sono', xp: 15, completion: 57, dropoff: 43 },
  { habit: 'Humor', xp: 20, completion: 83, dropoff: 17 },
]

export const moodDistribution = [
  { mood: 'Ótimo', value: 24 },
  { mood: 'Bem', value: 38 },
  { mood: 'Neutro', value: 21 },
  { mood: 'Cansado', value: 11 },
  { mood: 'Esgotado', value: 6 },
]

export const participationSeries = Array.from({ length: 14 }, (_, i) => ({
  day: `${i + 9}/09`,
  participation: Math.round(78 + Math.sin(i / 2) * 5),
  completion: Math.round(60 + Math.sin(i / 2.2) * 6),
}))

/* ------------------------------------------------------------------ *
 * Gamification
 * ------------------------------------------------------------------ */

export const xpDistribution = [
  { range: '0–24 Bee', users: 9800 },
  { range: '25–49 Bee', users: 16400 },
  { range: '50–74 Bee', users: 28100 },
  { range: '75–99 Bee', users: 21700 },
  { range: '100 Bee', users: 31200 },
]

/** Level names mirror data/levels.ts — the single source of truth. */
export const levelDistribution = [
  { level: 'Iniciante', users: 24100 },
  { level: 'Aprendiz', users: 38600 },
  { level: 'Guerreiro', users: 31900 },
  { level: 'Mestre', users: 19800 },
  { level: 'Lenda', users: 14030 },
]

export const streakBuckets = [
  { bucket: '1–3 dias', users: 18200 },
  { bucket: '4–7 dias', users: 9400 },
  { bucket: '8–14 dias', users: 2600 },
  { bucket: '15–30 dias', users: 780 },
  { bucket: '30+ dias', users: 228 },
]

export const progressionRules: { id: string; label: L10n; value: string; locked: boolean }[] = [
  { id: 'base', label: { 'pt-BR': 'Base do Daily Check', 'en-US': 'Daily Check base' }, value: '100 Bee', locked: true },
  { id: 'gate', label: { 'pt-BR': 'Liberação do Post completo', 'en-US': 'Full Post gate' }, value: '50 Bee no dia', locked: true },
  { id: 'photo', label: { 'pt-BR': 'Bônus da Foto do Dia', 'en-US': 'Photo of the Day bonus' }, value: '+10 Bee (fora da base)', locked: true },
  { id: 'pulse', label: { 'pt-BR': 'Pulse', 'en-US': 'Pulse' }, value: 'Sempre disponível', locked: true },
]

export interface AdminBadge {
  id: string
  name: string
  rule: L10n
  unlocked: string
  status: 'active' | 'draft'
  created: string
}

export const adminBadges: AdminBadge[] = [
  { id: 'b1', name: 'Primeiro Check', rule: { 'pt-BR': 'Fechar o primeiro dia em 100 Bee', 'en-US': 'Close the first day at 100 Bee' }, unlocked: '96.204', status: 'active', created: '12/01/2026' },
  { id: 'b2', name: 'Foco Total', rule: { 'pt-BR': 'Sequência de 3 dias', 'en-US': '3-day streak' }, unlocked: '61.480', status: 'active', created: '12/01/2026' },
  { id: 'b3', name: 'Semana de Ferro', rule: { 'pt-BR': 'Sequência de 7 dias', 'en-US': '7-day streak' }, unlocked: '28.911', status: 'active', created: '12/01/2026' },
  { id: 'b4', name: 'Imparável', rule: { 'pt-BR': 'Sequência de 14 dias', 'en-US': '14-day streak' }, unlocked: '9.744', status: 'active', created: '04/03/2026' },
  { id: 'b5', name: 'Lenda Viva', rule: { 'pt-BR': 'Sequência de 30 dias', 'en-US': '30-day streak' }, unlocked: '1.208', status: 'active', created: '04/03/2026' },
]

export const challenges = [
  { id: 'c1', name: 'Setembro consistente', period: '01/09 – 30/09/2026', participants: '42.118', status: 'active' as const },
  { id: 'c2', name: 'Semana do sono', period: '06/10 – 12/10/2026', participants: '—', status: 'draft' as const },
]

/* ------------------------------------------------------------------ *
 * Sports and community analytics
 * ------------------------------------------------------------------ */

export const sportAdoption = [
  { sport: 'Corrida', users: 41200, adoption: 32, frequency: 3.4, trend: 5.2 },
  { sport: 'Musculação', users: 38700, adoption: 30, frequency: 3.9, trend: 4.1 },
  { sport: 'Ciclismo', users: 18400, adoption: 14, frequency: 2.6, trend: 2.8 },
  { sport: 'CrossFit', users: 12900, adoption: 10, frequency: 3.1, trend: -1.4 },
  { sport: 'Natação', users: 8600, adoption: 7, frequency: 2.2, trend: 1.1 },
]

export const metricUsage = [
  { metric: 'Pace médio', usage: 74 },
  { metric: 'PR 5K / 10K', usage: 61 },
  { metric: '1RM', usage: 48 },
  { metric: 'FTP / W/kg', usage: 27 },
  { metric: 'Pace /100m', usage: 14 },
]

export const communityMetrics: AdminMetric[] = [
  // Every KPI card is a FIXED 30-day window (vs. the previous 30 days). The period selector never changes them.
  { id: 'posts', label: { 'pt-BR': 'Posts criados (30d)', 'en-US': 'Posts created (30d)' }, value: '81.204', delta: 3.1, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'pulses', label: { 'pt-BR': 'Pulses criados (30d)', 'en-US': 'Pulses created (30d)' }, value: '264.910', delta: 7.8, accent: true, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'comments', label: { 'pt-BR': 'Comentários (30d)', 'en-US': 'Comments (30d)' }, value: '149.377', delta: 2.2, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'likes', label: { 'pt-BR': 'Curtidas (30d)', 'en-US': 'Likes (30d)' }, value: '1.204.880', delta: 4.6, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'saves', label: { 'pt-BR': 'Salvos (30d)', 'en-US': 'Saves (30d)' }, value: '92.410', delta: 5.9, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'follows', label: { 'pt-BR': 'Novos seguidores (30d)', 'en-US': 'New follows (30d)' }, value: '58.302', delta: 1.4, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'creators', label: { 'pt-BR': 'Criadores ativos (30d)', 'en-US': 'Active creators (30d)' }, value: '22.640', delta: 3.7, hint: { 'pt-BR': 'Janela fixa de 30 dias', 'en-US': 'Fixed 30-day window' } },
  { id: 'balance', label: { 'pt-BR': 'Check antes de postar (30d)', 'en-US': 'Check before posting (30d)' }, value: '88,1%', delta: 2.0, hint: { 'pt-BR': 'Posts publicados após o Daily Check · janela fixa', 'en-US': 'Posts published after the Daily Check · fixed window' } },
]

export const engagementSeries = Array.from({ length: 12 }, (_, i) => ({
  week: `S${i + 1}`,
  posts: Math.round(6100 + Math.sin(i / 2) * 420 + i * 60),
  pulses: Math.round(19800 + Math.sin(i / 1.8) * 900 + i * 210),
  comments: Math.round(11200 + Math.sin(i / 2.4) * 600 + i * 90),
}))

export type CommunityRange = '7d' | '30d' | '90d' | '12m'

/** Engagement trend per selectable period (the only thing the period selector controls). */
export const engagementSeriesByRange: Record<
  CommunityRange,
  { label: string; posts: number; pulses: number; comments: number }[]
> = {
  '7d': ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((label, i) => ({
    label,
    posts: Math.round(2700 + Math.sin(i / 1.3) * 180),
    pulses: Math.round(8800 + Math.sin(i / 1.1) * 520),
    comments: Math.round(4900 + Math.sin(i / 1.6) * 260),
  })),
  '30d': Array.from({ length: 30 }, (_, i) => ({
    label: `${i + 1}`,
    posts: Math.round(2600 + Math.sin(i / 3) * 210 + i * 6),
    pulses: Math.round(8500 + Math.sin(i / 2.6) * 600 + i * 20),
    comments: Math.round(4800 + Math.sin(i / 3.4) * 300 + i * 8),
  })),
  '90d': Array.from({ length: 13 }, (_, i) => ({
    label: `S${i + 1}`,
    posts: Math.round(6100 + Math.sin(i / 2) * 420 + i * 60),
    pulses: Math.round(19800 + Math.sin(i / 1.8) * 900 + i * 210),
    comments: Math.round(11200 + Math.sin(i / 2.4) * 600 + i * 90),
  })),
  '12m': ['Out', 'Nov', 'Dez', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set'].map(
    (label, i) => ({
      label,
      posts: Math.round(52000 + Math.sin(i / 2) * 4200 + i * 2400),
      pulses: Math.round(160000 + Math.sin(i / 1.8) * 9000 + i * 8800),
      comments: Math.round(96000 + Math.sin(i / 2.4) * 6000 + i * 4300),
    })
  ),
}

/* ------------------------------------------------------------------ *
 * Notifications and content
 * ------------------------------------------------------------------ */

export type CampaignType = 'operational' | 'health' | 'product' | 'campaign' | 'system'

/**
 * Campaign content model: title and message stored SEPARATELY per locale.
 * Both locales are required before a campaign can be scheduled or sent
 * (REQUIRES BACKEND validation — the Admin form check is UX only).
 */
export type CampaignLocale = 'pt-BR' | 'en-US'
export interface LocalizedCampaignText {
  title: string
  message: string
}
export type CampaignContent = Record<CampaignLocale, LocalizedCampaignText>

/** Lists every empty required field, e.g. ['pt-BR.title']. Empty list = ready to schedule. */
export function missingContentFields(content: CampaignContent): string[] {
  const missing: string[] = []
  ;(['pt-BR', 'en-US'] as CampaignLocale[]).forEach((locale) => {
    ;(['title', 'message'] as (keyof LocalizedCampaignText)[]).forEach((field) => {
      if (!content[locale][field].trim()) missing.push(`${locale}.${field}`)
    })
  })
  return missing
}
export type CampaignStatus = 'draft' | 'scheduled' | 'sent'

export interface AdminCampaign {
  id: string
  title: string
  type: CampaignType
  status: CampaignStatus
  audience: L10n
  languages: string
  when: string
  reach?: string
  /**
   * Automated health-based segment. The Admin only ever sees the definition,
   * an estimated size and eligibility — never the user list or individual
   * health values. REQUIRES BACKEND: eligibility computed server-side,
   * respecting consent and notification preferences.
   */
  healthSegment?: { estimate: string; eligibility: L10n }
}

export const campaigns: AdminCampaign[] = [
  { id: 'n1', title: 'Lembrete do Daily Check — 20h', type: 'health', status: 'sent', audience: { 'pt-BR': 'Sem check hoje', 'en-US': 'No check today' }, languages: 'pt-BR · en-US', when: '21/09/2026 20:00', reach: '38.110' },
  { id: 'n2', title: 'Seu Daily Check ainda não fechou', type: 'health', status: 'scheduled', audience: { 'pt-BR': 'Daily Check ainda não fechado hoje', 'en-US': 'Daily Check not closed today' }, languages: 'pt-BR · en-US', when: '22/09/2026 21:30' },
  { id: 'n3', title: 'Novidade: gráficos de evolução corporal', type: 'product', status: 'sent', audience: { 'pt-BR': 'Todos os usuários', 'en-US': 'All users' }, languages: 'pt-BR · en-US', when: '18/09/2026 10:00', reach: '126.900' },
  { id: 'n4', title: 'Manutenção programada no domingo', type: 'operational', status: 'scheduled', audience: { 'pt-BR': 'Todos os usuários', 'en-US': 'All users' }, languages: 'pt-BR · en-US', when: '27/09/2026 08:00' },
  { id: 'n5', title: 'Desafio Semana do Sono', type: 'campaign', status: 'draft', audience: { 'pt-BR': 'Usuários com sono abaixo de 60%', 'en-US': 'Users with sleep under 60%' }, languages: 'pt-BR', when: '—', healthSegment: { estimate: '~8.400', eligibility: { 'pt-BR': 'Elegível · só com consentimento e notificações de saúde ativas', 'en-US': 'Eligible · only with consent and health notifications on' } } },
  { id: 'n6', title: 'Confirmação de novo dispositivo', type: 'system', status: 'sent', audience: { 'pt-BR': 'Disparo individual', 'en-US': 'Individual trigger' }, languages: 'pt-BR · en-US', when: 'contínuo', reach: '4.208' },
]

export type ContentType = 'educational' | 'featured' | 'onboarding' | 'help' | 'banner' | 'announcement'

export interface AdminContent {
  id: string
  title: string
  type: ContentType
  status: 'published' | 'draft' | 'scheduled'
  languages: string
  updated: string
  author: string
}

export const adminContent: AdminContent[] = [
  { id: 'ct1', title: 'Como o Life Score é calculado', type: 'educational', status: 'published', languages: 'pt-BR · en-US', updated: '19/09/2026', author: 'Marina Alencar' },
  { id: 'ct2', title: 'Boas-vindas — passo 1 de 4', type: 'onboarding', status: 'published', languages: 'pt-BR · en-US', updated: '12/09/2026', author: 'Marina Alencar' },
  { id: 'ct3', title: 'Destaque da semana: consistência no sono', type: 'featured', status: 'draft', languages: 'pt-BR', updated: '21/09/2026', author: 'Bruno Sato' },
  { id: 'ct4', title: 'Por que o Post pede 50 Bee', type: 'help', status: 'published', languages: 'pt-BR · en-US', updated: '02/08/2026', author: 'Marina Alencar' },
  { id: 'ct5', title: 'Banner — Desafio de setembro', type: 'banner', status: 'draft', languages: 'pt-BR', updated: '01/09/2026', author: 'Bruno Sato' },
  { id: 'ct6', title: 'Aviso — nova política de privacidade', type: 'announcement', status: 'draft', languages: 'pt-BR · en-US', updated: '20/09/2026', author: 'Helena Prado' },
]

/* ------------------------------------------------------------------ *
 * Exports
 * ------------------------------------------------------------------ */

export interface ExportJob {
  id: string
  name: L10n
  format: 'CSV' | 'XLSX' | 'PDF'
  range: string
  status: 'ready' | 'processing' | 'failed'
  size?: string
  requested: string
  by: string
}

export const exportJobs: ExportJob[] = [
  { id: 'e1', name: { 'pt-BR': 'Estatísticas de usuários', 'en-US': 'User statistics' }, format: 'CSV', range: '01/09 – 22/09', status: 'ready', size: '4,2 MB', requested: 'hoje, 08:10', by: 'Marina Alencar' },
  { id: 'e2', name: { 'pt-BR': 'Agregados do Daily Check', 'en-US': 'Daily Check aggregates' }, format: 'XLSX', range: '01/08 – 31/08', status: 'ready', size: '1,8 MB', requested: 'ontem, 17:42', by: 'Bruno Sato' },
  { id: 'e3', name: { 'pt-BR': 'Eventos de auditoria', 'en-US': 'Audit events' }, format: 'PDF', range: '01/09 – 22/09', status: 'processing', requested: 'hoje, 09:31', by: 'Helena Prado' },
  { id: 'e4', name: { 'pt-BR': 'Moderação e denúncias', 'en-US': 'Moderation and reports' }, format: 'CSV', range: '01/07 – 30/09', status: 'failed', requested: '20/09, 14:02', by: 'Diego Sampaio' },
]

/**
 * Export scope drives the privacy notice and the permission required.
 * aggregate → no user identification · operational → identifiable operational
 * records · audit → administrative actors and entity identifiers.
 * Sensitive health data never enters any of these exports.
 */
export type ExportScope = 'aggregate' | 'operational' | 'audit'

export const exportTemplates: { id: string; label: L10n; hint: L10n; aggregate: boolean; scope: ExportScope }[] = [
  { id: 'users', label: { 'pt-BR': 'Estatísticas de usuários', 'en-US': 'User statistics' }, hint: { 'pt-BR': 'Contagens por status, papel e origem.', 'en-US': 'Counts by status, role and source.' }, aggregate: true, scope: 'aggregate' },
  { id: 'engagement', label: { 'pt-BR': 'Engajamento', 'en-US': 'Engagement' }, hint: { 'pt-BR': 'Sessões, posts, Pulses e interações.', 'en-US': 'Sessions, posts, Pulses and interactions.' }, aggregate: true, scope: 'aggregate' },
  { id: 'dailycheck', label: { 'pt-BR': 'Agregados do Daily Check', 'en-US': 'Daily Check aggregates' }, hint: { 'pt-BR': 'Participação e conclusão por hábito. Sem dados individuais de saúde.', 'en-US': 'Participation and completion by habit. No individual health data.' }, aggregate: true, scope: 'aggregate' },
  { id: 'gamification', label: { 'pt-BR': 'Gamificação', 'en-US': 'Gamification' }, hint: { 'pt-BR': 'Bee, níveis, sequências e medalhas.', 'en-US': 'Bee, levels, streaks and badges.' }, aggregate: true, scope: 'aggregate' },
  { id: 'community', label: { 'pt-BR': 'Comunidade', 'en-US': 'Community' }, hint: { 'pt-BR': 'Criação de conteúdo e interações.', 'en-US': 'Content creation and interactions.' }, aggregate: true, scope: 'aggregate' },
  { id: 'moderation', label: { 'pt-BR': 'Moderação', 'en-US': 'Moderation' }, hint: { 'pt-BR': 'Ações, filas e tempo de resposta.', 'en-US': 'Actions, queues and response time.' }, aggregate: false, scope: 'operational' },
  { id: 'audit', label: { 'pt-BR': 'Eventos de auditoria', 'en-US': 'Audit events' }, hint: { 'pt-BR': 'Registro completo de ações administrativas.', 'en-US': 'Full record of administrative actions.' }, aggregate: false, scope: 'audit' },
]

/* ------------------------------------------------------------------ *
 * Team, roles and audit
 * ------------------------------------------------------------------ */

export type TeamRole = 'superadmin' | 'admin' | 'moderator' | 'content' | 'analyst' | 'support'

export const roleLabels: Record<TeamRole, L10n> = {
  superadmin: { 'pt-BR': 'Super Admin', 'en-US': 'Super Admin' },
  admin: { 'pt-BR': 'Admin', 'en-US': 'Admin' },
  moderator: { 'pt-BR': 'Moderador', 'en-US': 'Moderator' },
  content: { 'pt-BR': 'Gestor de conteúdo', 'en-US': 'Content Manager' },
  analyst: { 'pt-BR': 'Analista', 'en-US': 'Analyst' },
  support: { 'pt-BR': 'Suporte', 'en-US': 'Support' },
}

export interface TeamMember {
  id: string
  name: string
  email: string
  role: TeamRole
  status: 'active' | 'invited' | 'suspended'
  lastAccess: string
  twoFactor: boolean
}

export const team: TeamMember[] = [
  { id: 't1', name: 'Marina Alencar', email: 'marina@beelyv.com', role: 'superadmin', status: 'active', lastAccess: 'hoje, 09:42', twoFactor: true },
  { id: 't2', name: 'Bruno Sato', email: 'bruno@beelyv.com', role: 'admin', status: 'active', lastAccess: 'hoje, 08:05', twoFactor: true },
  { id: 't3', name: 'Helena Prado', email: 'helena@beelyv.com', role: 'moderator', status: 'active', lastAccess: 'ontem, 21:18', twoFactor: true },
  { id: 't4', name: 'Diego Sampaio', email: 'diego@beelyv.com', role: 'moderator', status: 'active', lastAccess: 'ontem, 19:02', twoFactor: false },
  { id: 't5', name: 'Cecília Nardi', email: 'cecilia@beelyv.com', role: 'content', status: 'active', lastAccess: 'há 3 dias', twoFactor: true },
  { id: 't6', name: 'Otávio Reis', email: 'otavio@beelyv.com', role: 'analyst', status: 'active', lastAccess: 'há 5 dias', twoFactor: true },
  { id: 't7', name: 'Lia Fontes', email: 'lia@beelyv.com', role: 'support', status: 'invited', lastAccess: '—', twoFactor: false },
  { id: 't8', name: 'Ricardo Maia', email: 'ricardo@beelyv.com', role: 'analyst', status: 'suspended', lastAccess: 'há 2 meses', twoFactor: false },
]

export type Permission = 'view' | 'create' | 'edit' | 'delete' | 'moderate' | 'export' | 'manage'

export const permissionLabels: Record<Permission, L10n> = {
  view: { 'pt-BR': 'Ver', 'en-US': 'View' },
  create: { 'pt-BR': 'Criar', 'en-US': 'Create' },
  edit: { 'pt-BR': 'Editar', 'en-US': 'Edit' },
  delete: { 'pt-BR': 'Excluir', 'en-US': 'Delete' },
  moderate: { 'pt-BR': 'Moderar', 'en-US': 'Moderate' },
  export: { 'pt-BR': 'Exportar', 'en-US': 'Export' },
  manage: { 'pt-BR': 'Gerir permissões', 'en-US': 'Manage permissions' },
}

export const permissionAreas: { id: string; label: L10n }[] = [
  { id: 'users', label: { 'pt-BR': 'Usuários', 'en-US': 'Users' } },
  { id: 'content', label: { 'pt-BR': 'Conteúdo', 'en-US': 'Content' } },
  { id: 'moderation', label: { 'pt-BR': 'Moderação', 'en-US': 'Moderation' } },
  { id: 'analytics', label: { 'pt-BR': 'Análise', 'en-US': 'Analytics' } },
  { id: 'communications', label: { 'pt-BR': 'Comunicações', 'en-US': 'Communications' } },
  { id: 'reports', label: { 'pt-BR': 'Relatórios', 'en-US': 'Reports' } },
  { id: 'settings', label: { 'pt-BR': 'Configurações', 'en-US': 'Settings' } },
  { id: 'team', label: { 'pt-BR': 'Equipe', 'en-US': 'Team' } },
  { id: 'audit', label: { 'pt-BR': 'Auditoria', 'en-US': 'Audit' } },
]

/** role → area → permissions granted. */
export const permissionMatrix: Record<TeamRole, Record<string, Permission[]>> = {
  superadmin: permissionAreas.reduce<Record<string, Permission[]>>((acc, area) => {
    acc[area.id] = ['view', 'create', 'edit', 'delete', 'moderate', 'export', 'manage']
    return acc
  }, {}),
  admin: {
    users: ['view', 'edit', 'export'],
    content: ['view', 'create', 'edit', 'delete'],
    moderation: ['view', 'moderate'],
    analytics: ['view', 'export'],
    communications: ['view', 'create', 'edit'],
    reports: ['view', 'export'],
    settings: ['view', 'edit'],
    team: ['view', 'create', 'edit'],
    audit: ['view', 'export'],
  },
  moderator: {
    users: ['view'],
    content: ['view'],
    moderation: ['view', 'moderate'],
    analytics: ['view'],
    communications: [],
    reports: ['view'],
    settings: [],
    team: [],
    audit: ['view'],
  },
  content: {
    users: [],
    content: ['view', 'create', 'edit', 'delete'],
    moderation: ['view'],
    analytics: ['view'],
    communications: ['view', 'create', 'edit'],
    reports: ['view'],
    settings: [],
    team: [],
    audit: [],
  },
  analyst: {
    users: ['view'],
    content: ['view'],
    moderation: [],
    analytics: ['view', 'export'],
    communications: [],
    reports: ['view', 'export'],
    settings: [],
    team: [],
    audit: ['view'],
  },
  support: {
    users: ['view', 'edit'],
    content: ['view'],
    moderation: ['view'],
    analytics: [],
    communications: [],
    reports: [],
    settings: [],
    team: [],
    audit: [],
  },
}

export interface AuditRecord {
  id: string
  admin: string
  role: TeamRole
  action: L10n
  entity: string
  timestamp: string
  context: L10n
  before?: string
  after?: string
  severity: 'info' | 'sensitive'
}

export const auditLog: AuditRecord[] = [
  { id: 'a1', admin: 'Helena Prado', role: 'moderator', action: { 'pt-BR': 'Removeu post', 'en-US': 'Removed post' }, entity: 'post-2', timestamp: '22/09/2026 09:12', context: { 'pt-BR': 'Denúncia R-2841 · conteúdo enganoso sobre saúde', 'en-US': 'Report R-2841 · misleading health content' }, before: 'published', after: 'removed', severity: 'sensitive' },
  { id: 'a2', admin: 'Marina Alencar', role: 'superadmin', action: { 'pt-BR': 'Alterou permissões de papel', 'en-US': 'Changed role permissions' }, entity: 'role:analyst', timestamp: '22/09/2026 08:47', context: { 'pt-BR': 'Concedido Exportar em Relatórios', 'en-US': 'Granted Export on Reports' }, before: 'view', after: 'view, export', severity: 'sensitive' },
  { id: 'a3', admin: 'Bruno Sato', role: 'admin', action: { 'pt-BR': 'Agendou notificação', 'en-US': 'Scheduled notification' }, entity: 'campaign:n2', timestamp: '22/09/2026 08:20', context: { 'pt-BR': 'Saúde · Daily Check ainda não fechado · 21:30', 'en-US': 'Health · Daily Check not closed yet · 9:30pm' }, severity: 'info' },
  { id: 'a4', admin: 'Diego Sampaio', role: 'moderator', action: { 'pt-BR': 'Advertiu usuário', 'en-US': 'Warned user' }, entity: 'u5', timestamp: '21/09/2026 16:44', context: { 'pt-BR': 'Promessa de resultado sem base', 'en-US': 'Unsupported results claim' }, severity: 'sensitive' },
  { id: 'a5', admin: 'Cecília Nardi', role: 'content', action: { 'pt-BR': 'Publicou conteúdo educativo', 'en-US': 'Published educational content' }, entity: 'ct1', timestamp: '19/09/2026 11:02', context: { 'pt-BR': 'Versões pt-BR e en-US', 'en-US': 'pt-BR and en-US versions' }, severity: 'info' },
  { id: 'a6', admin: 'Otávio Reis', role: 'analyst', action: { 'pt-BR': 'Exportou agregados', 'en-US': 'Exported aggregates' }, entity: 'export:e2', timestamp: '21/09/2026 17:42', context: { 'pt-BR': 'Daily Check · agosto · sem dados individuais', 'en-US': 'Daily Check · August · no individual data' }, severity: 'info' },
  { id: 'a7', admin: 'Marina Alencar', role: 'superadmin', action: { 'pt-BR': 'Suspendeu conta', 'en-US': 'Suspended account' }, entity: 'u6', timestamp: '20/09/2026 10:15', context: { 'pt-BR': 'Reincidência em spam', 'en-US': 'Repeated spam' }, before: 'active', after: 'suspended', severity: 'sensitive' },
  { id: 'a8', admin: 'Bruno Sato', role: 'admin', action: { 'pt-BR': 'Ativou modo manutenção', 'en-US': 'Enabled maintenance mode' }, entity: 'platform', timestamp: '15/09/2026 03:00', context: { 'pt-BR': 'Janela de 22 minutos', 'en-US': '22-minute window' }, before: 'off', after: 'on', severity: 'sensitive' },
]

/**
 * Prototype audit writer: confirmed high-impact actions append here so they
 * show up in Admin → Auditoria. REQUIRES BACKEND: the server writes an
 * append-only record for every such action; the client never writes audit.
 */
export function recordAudit(
  entry: Omit<AuditRecord, 'id' | 'timestamp' | 'admin' | 'role'>
): AuditRecord {
  const record: AuditRecord = {
    id: `a-${Date.now()}-${auditLog.length}`,
    admin: prototypeActorName(),
    role: prototypeAdminRole,
    timestamp: nowStamp(),
    ...entry,
  }
  auditLog.unshift(record)
  return record
}

function nowStamp(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(
    now.getHours()
  )}:${pad(now.getMinutes())}`
}

/* ------------------------------------------------------------------ *
 * Sensitive health access — individual, exceptional grants.
 * NEVER inherited from a role (Super Admin included).
 * ------------------------------------------------------------------ */

export interface SensitiveHealthGrant {
  id: string
  grantee: string
  grantedBy: string
  grantedAt: string
  justification: string
  expiresAt?: string
}

/** No active grants at start. Prototype grants live in memory until reload. */
export const sensitiveHealthGrants: SensitiveHealthGrant[] = []

/**
 * Who may ISSUE a grant: holders of Equipe · Gerir permissões. Issuing a grant
 * never gives the issuer the access; self-grants are refused.
 * REQUIRES BACKEND: authorization, persistence, immutable audit.
 */
export function canIssueSensitiveHealthGrant(role: TeamRole): boolean {
  return (permissionMatrix[role].team ?? []).includes('manage')
}

export function grantSensitiveHealthAccess(grantee: string, justification: string): SensitiveHealthGrant {
  const grant: SensitiveHealthGrant = {
    id: `g-${Date.now()}`,
    grantee,
    grantedBy: prototypeActorName(),
    grantedAt: nowStamp(),
    justification,
  }
  sensitiveHealthGrants.unshift(grant)
  recordAudit({
    action: { 'pt-BR': 'Concedeu acesso a dados sensíveis de saúde', 'en-US': 'Granted sensitive health data access' },
    entity: `member:${grantee}`,
    context: { 'pt-BR': `Concessão individual · Justificativa: ${justification}`, 'en-US': `Individual grant · Justification: ${justification}` },
    before: 'sem acesso',
    after: 'acesso individual',
    severity: 'sensitive',
  })
  return grant
}

export function revokeSensitiveHealthAccess(grantId: string) {
  const index = sensitiveHealthGrants.findIndex((grant) => grant.id === grantId)
  if (index < 0) return
  const [grant] = sensitiveHealthGrants.splice(index, 1)
  recordAudit({
    action: { 'pt-BR': 'Revogou acesso a dados sensíveis de saúde', 'en-US': 'Revoked sensitive health data access' },
    entity: `member:${grant.grantee}`,
    context: {
      'pt-BR': `Concessão de ${grant.grantedAt} por ${grant.grantedBy}`,
      'en-US': `Grant from ${grant.grantedAt} by ${grant.grantedBy}`,
    },
    before: 'acesso individual',
    after: 'sem acesso',
    severity: 'sensitive',
  })
}

/* ------------------------------------------------------------------ *
 * High-impact actions and the explicit permission each requires.
 * All require confirmation and produce an audit record.
 * ------------------------------------------------------------------ */

export const highImpactActions: { action: L10n; requires: L10n }[] = [
  { action: { 'pt-BR': 'Excluir usuário', 'en-US': 'Delete user' }, requires: { 'pt-BR': 'Usuários · Excluir', 'en-US': 'Users · Delete' } },
  { action: { 'pt-BR': 'Suspender / reativar conta', 'en-US': 'Suspend / reactivate account' }, requires: { 'pt-BR': 'Usuários · Editar', 'en-US': 'Users · Edit' } },
  { action: { 'pt-BR': 'Excluir conteúdo', 'en-US': 'Delete content' }, requires: { 'pt-BR': 'Conteúdo · Excluir ou Moderação · Moderar', 'en-US': 'Content · Delete or Moderation · Moderate' } },
  { action: { 'pt-BR': 'Alterar permissões ou papel de membro', 'en-US': 'Change permissions or member role' }, requires: { 'pt-BR': 'Equipe · Gerir permissões (só Super Admin)', 'en-US': 'Team · Manage permissions (Super Admin only)' } },
  { action: { 'pt-BR': 'Suspender, reativar ou remover membro; redefinir 2FA', 'en-US': 'Suspend, reactivate or remove member; reset 2FA' }, requires: { 'pt-BR': 'Equipe · Gerir permissões', 'en-US': 'Team · Manage permissions' } },
  { action: { 'pt-BR': 'Alterar regra de hidratação', 'en-US': 'Change hydration rule' }, requires: { 'pt-BR': 'Especial · Alterar regra de hidratação', 'en-US': 'Special · Change hydration rule' } },
  { action: { 'pt-BR': 'Gerenciar integrações', 'en-US': 'Manage integrations' }, requires: { 'pt-BR': 'Especial · Gerenciar integrações', 'en-US': 'Special · Manage integrations' } },
  { action: { 'pt-BR': 'Alterar parâmetros de engajamento', 'en-US': 'Change engagement parameters' }, requires: { 'pt-BR': 'Especial · Gerenciar parâmetros de engajamento', 'en-US': 'Special · Manage engagement parameters' } },
  { action: { 'pt-BR': 'Exportar eventos de auditoria', 'en-US': 'Export audit events' }, requires: { 'pt-BR': 'Auditoria · Exportar e Relatórios · Exportar', 'en-US': 'Audit · Export and Reports · Export' } },
  { action: { 'pt-BR': 'Ver dados sensíveis de saúde', 'en-US': 'View sensitive health data' }, requires: { 'pt-BR': 'Concessão individual com justificativa', 'en-US': 'Individual grant with justification' } },
]
